import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { actionOnBroken, actionOnClosed, actionOnError, actionOnMessage } from '@skynet/data-ws-mm';
import type { GenericError } from '@skynet/data-ws-core';
import { parseResponse } from '@skynet/data-ws-core';
import type {
  searchArchivedLoans,
  searchArchivedRetainers,
  showLoan,
  showRetainer,
} from './actions';
import type { ArchivedItem, ArchiveState, ShowLoanPayload, ShowRetainerPayload } from './types';

/**
 * Both restore commands carry the item id, under a domain-specific key. Reading
 * it here keeps `createArchiveSlice` non-generic: making it generic over the
 * thunk would put `RootState` (via the app thunk config) into the returned
 * slice's type and close a type cycle back through the root reducer.
 */
const readRestoreId = (arg: ShowLoanPayload | ShowRetainerPayload): number =>
  'loanID' in arg ? arg.loanID : arg.projectID;

/**
 * Reason shown for a restore whose ack can no longer arrive because the socket
 * went away while the command was in flight. Whether the server applied it is
 * genuinely unknown at that point — the command may well have landed and only
 * the ack been lost — so both this copy and the `unconfirmed` status it travels
 * with say so rather than claiming the restore was rejected.
 */
const CONNECTION_LOST_DESCRIPTION =
  'the connection dropped before the server confirmed it. Reopen the page to check whether it went through.';

/**
 * A failure envelope carries no `message` of its own in some cases, which the
 * registered `getDescription` turns into an empty string. Normalising that to
 * `null` lets the notification fall back to its own copy instead of trailing a
 * colon with nothing after it.
 */
const readErrorDescription = (error: GenericError | null | undefined): string | null =>
  error?.description ? error.description : null;

/** The only fields this slice reads off an archive-channel message. */
interface ArchiveMessage {
  command: string;
  list?: ArchivedItem[];
}

/**
 * Resetting on `resetStore` is the app's job: it wraps each mounted archive
 * reducer in `withResetState(reducer, initialState)`.
 */
export const initialState: ArchiveState = {
  archivedItems: [],
  pendingSearches: 0,
  searchSeq: 0,
  requestStatus: null,
  error: null,
  restoringId: null,
  restoringName: null,
  restoreOutcome: null,
};

export function createArchiveSlice({
  prefix,
  commands,
  searchAction,
  restoreAction,
}: {
  prefix: 'loans' | 'retainers';
  commands: {
    ArchivedItems: string;
    SearchArchivedItems: string;
    ShowItem: string;
  };
  searchAction: typeof searchArchivedLoans | typeof searchArchivedRetainers;
  /**
   * The thunk that sends the restore command (`ShowLoan` / `ShowRetainer`).
   * Its whole lifecycle drives the restore state below, so anything that
   * dispatches it is treated as an archive restore — see the note on the thunk.
   */
  restoreAction: typeof showLoan | typeof showRetainer;
}) {
  // `ShowItem` is listed so `parseResponse` accepts the shared envelope that
  // names it in `okCommand` / `errorCommand`. A bare `{ command: 'ShowLoan' }`
  // is not an ack shape on this channel: the slice used to read exactly that
  // and the restore gave no feedback at all (SKY-2642), which is only possible
  // if the server never sends it.
  const allowedCommands = [commands.ArchivedItems, commands.SearchArchivedItems, commands.ShowItem];

  return createSlice({
    name: `${prefix}/archive`,
    initialState,
    reducers: {
      /** Drops the outcome the page has just shown the user. */
      restoreOutcomeShown(state) {
        state.restoreOutcome = null;
      },
      /**
       * Drops the list this page is showing, along with the request state that
       * produced it.
       *
       * The list is only ever filled by a search, so whenever no search term is
       * in effect there is nothing legitimate to show. The page dispatches this
       * whenever its search box is empty — including on mount, since the term
       * lives in the URL while the results live here: without it, reopening the
       * page rendered the previous visit's results under an empty search box.
       *
       * Restore bookkeeping is deliberately left alone: clearing the search box
       * must not unlock the buttons or orphan the ack of a restore that is
       * still in flight.
       */
      clearList(state) {
        state.archivedItems = [];
        state.requestStatus = null;
        state.error = null;
        // Answers still on their way belong to a query that is no longer in
        // effect; forgetting them here is what stops them repopulating the list
        // under an empty search box.
        state.pendingSearches = 0;
      },
      /**
       * Gives up on search number `seq` when its answer has not arrived in time.
       *
       * An answer can go missing on a live socket, and nothing else would ever
       * settle it: the list would keep loading, and every later answer would be
       * dropped as belonging to an older query since the count never gets back
       * down. Settling it as failed shows the user the search did not come
       * back, and lets the next one start from a clean count.
       *
       * Only the newest search can time out. An older one's timer fires while a
       * newer search is still in its own window, and its answer is more likely
       * slow than lost — the newer search's timer covers both.
       *
       * If the answer turns up after all, it finds the count at zero and is
       * dropped: it answers a query this page has already reported as failed.
       */
      searchTimedOut(state, action: PayloadAction<number>) {
        if (state.pendingSearches === 0 || action.payload !== state.searchSeq) return;

        state.pendingSearches = 0;
        state.requestStatus = 'error';
      },
      /**
       * Drops whatever restore this slice thought was in flight. The archive
       * page calls this on mount: an ack that never arrived would otherwise
       * keep `restoringId` set and every restore button disabled for the rest
       * of the session. (A socket that drops while the page stays open is
       * handled without waiting for a remount — see the matcher below.)
       *
       * Trade-off: if the ack for the dropped restore arrives after this runs
       * (the user left and came back before the server answered), it now finds
       * `restoringId` cleared and is ignored — see the early return below. The
       * buttons unlock as intended, but that ack produces no feedback at all:
       * no success or failure notification, and a successful restore's row is
       * not pruned — it stays in `archivedItems` until the next full list push.
       */
      resetRestore(state) {
        state.restoringId = null;
        state.restoringName = null;
        state.restoreOutcome = null;
      },
    },
    extraReducers: (builder) => {
      builder.addCase(searchAction.pending, (state) => {
        state.requestStatus = 'pending';
        state.error = null;
        state.pendingSearches += 1;
        state.searchSeq += 1;
        // The rows on screen answer the previous query. Dropping them here means
        // the list is only ever empty, loading, or the answer to the search that
        // is actually in effect — never the stale answer to an older one.
        state.archivedItems = [];
      });

      builder.addCase(searchAction.rejected, (state) => {
        // Nothing reached the server, so no answer is owed for this one — but
        // one may still be for a search sent before it.
        state.pendingSearches = Math.max(0, state.pendingSearches - 1);
        if (state.pendingSearches === 0) {
          state.requestStatus = 'error';
        }
      });

      builder.addCase(restoreAction.pending, (state, action) => {
        // One restore at a time. The ack carries no id, so a second request
        // started on top of this one would have its ack matched against the
        // wrong row. The thunk's `condition` already keeps that second command
        // off the wire; this is the backstop for the bookkeeping, because
        // `pending` is dispatched before the payload creator runs and a
        // reducer-only guard could not have stopped the send.
        if (state.restoringId !== null) return;

        const restoringId = readRestoreId(action.meta.arg);

        state.restoringId = restoringId;
        // Captured now rather than on the ack: a search that lands in between
        // replaces `archivedItems` with the server's filtered page, which need
        // not contain this row any more.
        state.restoringName =
          state.archivedItems.find((item) => item.id === restoringId)?.name ?? null;
        // `restoreOutcome` is left alone on purpose — see its doc comment.
      });

      // The executor only dispatches a plain WS send action and does not throw,
      // so this is defensive: it keeps the state machine total instead of
      // stranding every restore button disabled if the thunk ever does reject
      // before an ack could possibly arrive.
      builder.addCase(restoreAction.rejected, (state, action) => {
        // A rejection from a request the guard above turned away must not
        // cancel the one actually in flight.
        if (state.restoringId !== readRestoreId(action.meta.arg)) return;

        // Nothing reached the server, so there is nothing to announce.
        state.restoringId = null;
        state.restoringName = null;
      });

      builder.addCase(actionOnMessage, (state, action) => {
        const { json, skipProcessing, error, okCommand, errorCommand } =
          parseResponse<ArchiveMessage>(action.payload, allowedCommands);

        if (skipProcessing || !json) return;

        // A restore ack (ok or error) is only ever an answer to the request this
        // slice has in flight, so it is matched against `restoringId` rather than
        // the response, which carries no id.
        const isRestoreAck = okCommand === commands.ShowItem || errorCommand === commands.ShowItem;

        if (isRestoreAck) {
          if (state.restoringId === null) return;

          const restoredId = state.restoringId;

          if (errorCommand === commands.ShowItem) {
            state.restoreOutcome = {
              status: 'error',
              id: restoredId,
              name: state.restoringName,
              description: readErrorDescription(error),
            };
          } else {
            // The item is no longer archived — the server does not push a fresh
            // list, so drop the row here to keep the page honest.
            state.archivedItems = state.archivedItems.filter((item) => item.id !== restoredId);
            state.restoreOutcome = {
              status: 'success',
              id: restoredId,
              name: state.restoringName,
              description: null,
            };
          }

          state.restoringId = null;
          state.restoringName = null;
          return;
        }

        if (error) {
          // Same ordering rule as a successful answer below: a failure is an
          // answer too, and it belongs to the oldest search still owed one.
          if (state.pendingSearches === 0) return;

          state.pendingSearches -= 1;

          // An older query failed while a newer one is still on its way. The box
          // holds the newer term, so the list keeps waiting for its answer
          // rather than reporting a failure the user did not ask about.
          if (state.pendingSearches > 0) return;

          state.error = error;
          state.requestStatus = 'error';
          return;
        }

        const { command } = json;

        if (command === commands.ArchivedItems) {
          // No search is in effect: either the box was cleared while this answer
          // was on its way, or nothing asked for it at all.
          if (state.pendingSearches === 0) return;

          state.pendingSearches -= 1;

          // A newer search is still outstanding, so this answers an older query.
          // Its rows would be replaced moments later anyway — and would stick if
          // the newer answer had already overtaken it.
          if (state.pendingSearches > 0) return;

          const { list } = json;
          state.archivedItems = list || [];
          state.requestStatus = 'success';
          state.error = null;
        }
      });

      // The socket carrying the answers went away, so nothing this slice is
      // waiting for can arrive any more. Settle both in-flight requests instead
      // of leaving the row spinning, every restore button disabled and the list
      // loading until the page is reopened.
      //
      // These three are every terminal socket event the middleware dispatches
      // (`::CLOSED`, `::BROKEN`, `::ERROR`); there is no `::CLOSE`. `BROKEN` and
      // `ERROR` are also what a blip that auto-reconnects raises, which is why
      // the restore is reported as unconfirmed rather than failed: the command
      // may have reached the server and only its ack been lost.
      builder.addMatcher(isAnyOf(actionOnClosed, actionOnBroken, actionOnError), (state) => {
        // Only the `ArchivedItems` push clears a search, and it is never coming.
        if (state.requestStatus === 'pending') {
          state.requestStatus = 'error';
        }
        state.pendingSearches = 0;

        if (state.restoringId === null) return;

        state.restoreOutcome = {
          status: 'unconfirmed',
          id: state.restoringId,
          name: state.restoringName,
          description: CONNECTION_LOST_DESCRIPTION,
        };
        state.restoringId = null;
        state.restoringName = null;
      });
    },
  });
}
